import React, { createContext, useContext, ReactNode } from 'react';

/**
 * # Compact Mode Standard
 *
 * A shared mechanism for density (compact) variants of custom components.
 * When a parent declares "this region is compact," every density-aware
 * descendant picks it up automatically. Explicit per-call-site overrides
 * still win.
 *
 * ## How it flows
 *
 * Persisted user preferences (e.g. `useDisplayPreferences().compactOrderDetails`)
 * stay in `DisplayPreferencesContext`. A parent reads the preference and
 * bridges it into `DensityProvider`:
 *
 * ```tsx
 * const { compactOrderDetails } = useDisplayPreferences();
 * return (
 *   <DensityProvider compact={compactOrderDetails}>
 *     <Drawer>...</Drawer>
 *   </DensityProvider>
 * );
 * ```
 *
 * ## Adding compact mode to a component
 *
 * 1. Add `compact?: boolean` to the Props interface.
 * 2. **Do not default the prop.** Leave destructuring as `compact: compactProp`
 *    (or just `compact`), NOT `compact = false`. A `false` default makes the
 *    value always defined, which causes `useDensity` to skip the context
 *    fallback and forces every consumer to be comfortable.
 * 3. Resolve at the top of the component body:
 *    ```tsx
 *    const compact = useDensity(compactProp);
 *    ```
 * 4. Use a small helper for density-specific classes:
 *    ```tsx
 *    const cx = (normal: string, dense: string) => (compact ? dense : normal);
 *    ```
 * 5. Apply the helper only to the four density dimensions below.
 *
 * ## The four density dimensions
 *
 * These are the only things that change between modes. Everything else is
 * identical across modes.
 *
 * | Dimension              | Comfortable (default)        | Compact                                      |
 * |------------------------|------------------------------|----------------------------------------------|
 * | Padding                | `px-4 py-2.5` (or larger)    | one Tailwind step down: `px-2 py-1`          |
 * | Font size              | `text-sm` / `text-base`      | one step down: `text-xs` / `text-sm`         |
 * | Icon size              | `h-5 w-5`                    | `h-4 w-4` or `h-3.5 w-3.5`                   |
 * | Internal gap / margin  | `gap-2`, `mb-2`, `space-y-3` | `gap-1`, `mb-1`, `space-y-2`                 |
 *
 * ## Out of scope (don't change between modes)
 *
 * - Border widths, ring colors, stale-field highlight styles.
 * - `rounded-*` values (visual identity).
 * - Widths, grid templates, layout structure.
 * - Disabled / loading / focus affordances.
 * - Color tokens (theme tokens, dark-mode variants).
 *
 * If a density change pulls you toward modifying any of the above, stop —
 * that is a different concern.
 *
 * ## Migration notes for existing density-aware components
 *
 * - `TypeAhead` — migrated. Reads `useDensity(compactProp)`. The legs section
 *   in `OrderDetailsModalContent.tsx` no longer threads `compact={compact}`
 *   into TypeAhead call sites; the provider above the drawer's content
 *   subtree handles it.
 * - `TabBar` — exception, do NOT migrate. Its `compact: boolean` prop is a
 *   *visual style variant* (compact = pill-shaped breadcrumb tabs;
 *   comfortable = full-width divided tabs), not a density step. The order
 *   drawer hard-codes `compact={true}` regardless of the user's
 *   `compactOrderDetails` preference because the drawer always wants pill
 *   tabs. Migrating it to context would couple a UI choice to a density
 *   preference and cause regressions.
 * - `DateTimePicker` — exception. It exposes
 *   `inputVariant: 'default' | 'compact' | 'modal'` AND a deprecated
 *   `compact: boolean` that aliases `inputVariant='compact'`. The `'modal'`
 *   value conflates a visual style with a density. Today's call sites pass
 *   `inputVariant={compact ? 'compact' : 'modal'}` explicitly. **Do not**
 *   add a fresh `compact` prop alongside the deprecated alias. When a
 *   `'modal'`-styled, density-compact picker is needed, redesign that API
 *   then.
 * - `size`-prop components (ButtonGroup, MultiSelect, StatButton) — `size`
 *   describes element dimensions, not internal density. Don't conflate.
 *
 * ## What this standard does NOT cover
 *
 * - Tooling / lint rules to enforce the dimensions table.
 * - A separate `'spacious'` or `'dense'` third level (revisit if the user
 *   preference grows past a binary).
 * - Inline-table density (TanTable, Spreadsheet) — those have their own
 *   row-density mechanisms and aren't a compact-mode concern.
 */

const DensityContext = createContext<boolean>(false);
DensityContext.displayName = 'DensityContext';

interface DensityProviderProps {
  compact: boolean;
  children: ReactNode;
}

export const DensityProvider: React.FC<DensityProviderProps> = ({ compact, children }) => (
  <DensityContext.Provider value={compact}>{children}</DensityContext.Provider>
);

/**
 * Returns the effective compact state for the current subtree.
 *
 * Resolution order:
 *   1. `override` argument (explicit call-site control) wins if it is `true`
 *      or `false`.
 *   2. Nearest `DensityProvider` value.
 *   3. Default `false` (no provider mounted) — by design; "no provider" is
 *      comfortable.
 *
 * `useContext` is called unconditionally to satisfy the Rules of Hooks
 * regardless of whether `override` is provided.
 *
 * See the file-level JSDoc above for the component opt-in pattern and the
 * four density dimensions that may change between modes.
 */
export const useDensity = (override?: boolean): boolean => {
  const fromContext = useContext(DensityContext);
  return override ?? fromContext;
};
